import { useState, useEffect } from "react";

/**
 * Formats a URL to ensure it has the proper protocol for navigation
 * @param url The URL to format
 * @returns Properly formatted URL
 */
export const formatUrl = (url: string): string => {
  if (!url) return "";

  // If URL already has a protocol, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // If it's an external domain without protocol (contains dot and doesn't start with slash)
  if (!url.startsWith("/") && url.includes(".")) {
    return `https://${url}`;
  }

  // Return as is for internal paths
  return url;
};

const REFERRER_STORAGE_KEY = "app_referrer";

/**
 * Stores the referrer URL in localStorage
 * @param url The URL to store as referrer
 */
export const storeReferrer = (url: string): void => {
  if (url) {
    try {
      localStorage.setItem(REFERRER_STORAGE_KEY, url);
      console.log("Stored referrer:", url);
    } catch (error) {
      console.error("Failed to store referrer:", error);
    }
  }
};

/**
 * Gets the stored referrer from localStorage
 * @returns The stored referrer URL or empty string if none
 */
export const getStoredReferrer = (): string => {
  try {
    const referrer = localStorage.getItem(REFERRER_STORAGE_KEY);
    console.log("Retrieved stored referrer:", referrer);
    return referrer || "";
  } catch (error) {
    console.error("Failed to get stored referrer:", error);
    return "";
  }
};

/**
 * Clears the stored referrer from localStorage
 */
export const clearStoredReferrer = (): void => {
  try {
    localStorage.removeItem(REFERRER_STORAGE_KEY);
    console.log("Cleared stored referrer");
  } catch (error) {
    console.error("Failed to clear stored referrer:", error);
  }
};

/**
 * Hook to get the referrer URL for navigation
 * @param excludePaths Array of paths to exclude from being considered as valid referrers
 * @returns The referrer URL or empty string if no valid referrer
 */
export const useReferrer = (excludePaths: string[] = []): string => {
  const [referrer, setReferrer] = useState<string>("");

  useEffect(() => {
    // Get the stored referrer from localStorage
    const storedReferrer = getStoredReferrer();

    // Check if referrer should be excluded
    const shouldExclude = excludePaths.some((path) => storedReferrer?.includes(path));

    if (storedReferrer && !shouldExclude) {
      setReferrer(storedReferrer);
    } else {
      // If referrer is excluded or not available, return empty string
      setReferrer("");
    }
  }, [excludePaths]);

  return referrer;
};
